import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import * as XLSX from 'xlsx';

import { Box, Autocomplete, TextField, Dialog, DialogContent, DialogActions, Button } from '@mui/material';

import type { Game } from '@shared/types';
import { getFlagForLanguage } from '@shared/utils/flag-utils';
import { isGameExpired, formatGameEndDate } from '@shared/utils/gameExpiration';

import './Dashboard.scss';

interface PartnerGame {
  _id: string;
  gameName: string;
  name?: string; // Новое поле для имени модели игры
  notion: string;
  email?: string;
  key: string;
  gameBuildId: string;
  gameBuild?: any; // Объект gameBuild
  createdAt: string;
  activateDate?: string;
  gameDayCount: number;
  editDayCount: number;
  runCount: number;
  isActive: boolean;
  isCreated?: boolean;
}

interface PartnerProfile {
  id: string;
  email: string;
  apiKey: string;
  defaultGameDayCount: number;
  defaultEditDayCount: number;
  defaultIsWhiteLabel: boolean;
  createdAt: string;
  balance: string;
}

type PartnerAvailableGame = Pick<Game, 'title' | 'name' | 'price' | 'startGameImageExampleUrl' | 'endGameImageExampleUrl' | 'gameTileExampleUrl'>;

const Dashboard: React.FC = () => {
  const [games, setGames] = useState<PartnerGame[]>([]);
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({ 
    gameTitle: '', 
    notion: '', 
    keyCount: 1,
    urlButton: ''
  });
  const [availableGames, setAvailableGames] = useState<PartnerAvailableGame[]>([]);
  const [selectedGamePartner, setSelectedGamePartner] = useState<PartnerAvailableGame | null>(null);
  const [selectedPreviewPartner, setSelectedPreviewPartner] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [monthlyStats, setMonthlyStats] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Загрузка профиля и игр
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (error === 'Ошибка загрузки данных' || error === 'Ошибка загрузки профиля') {
      navigate('/partners/login');
    }
  }, [error, navigate]);

  // Функция для проверки истекших игр
  const checkExpiredGames = (gamesList: PartnerGame[]) => {
    return gamesList.map(game => {
      if (game.activateDate && game.gameDayCount) {
        const activateDate = new Date(game.activateDate);
        
        // Если игра истекла, но еще активна, обновляем статус
        if (isGameExpired(activateDate, game.gameDayCount) && game.isActive) {
          return { ...game, isActive: false };
        }
      }
      return game;
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Загружаем профиль
      const profileResponse = await fetch('/api/partner/profile', {
        credentials: 'include',
      });
      
      if (!profileResponse.ok) {
        throw new Error('Не авторизован');
      }
      
      const profileData = await profileResponse.json();
      setProfile(profileData.data.partner);

      // Загружаем игры
      const gamesResponse = await fetch('/api/partner/games', {
        credentials: 'include',
      });
      
      if (gamesResponse.ok) {
        const gamesData = await gamesResponse.json();
        const updatedGames = checkExpiredGames(gamesData.data.games);
        setGames(updatedGames);
        setMonthlyStats(calculateMonthlyStats(updatedGames));
      }

      // Загружаем доступные игры из коллекции games
      const availableGamesResponse = await fetch('/api/partner/available-games', {
        credentials: 'include',
      });
      
      if (availableGamesResponse.ok) {
        const availableGamesData = await availableGamesResponse.json();
        const sortedGames = availableGamesData.data.games.sort((a: PartnerAvailableGame, b: PartnerAvailableGame) => (a.name || a.title).localeCompare(b.name || b.title, 'ru'));
        setAvailableGames(sortedGames);
      } else {
        // Fallback — assume default games with mock data if needed
        setAvailableGames([]);
      }
      
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/partner/logout', {
        method: 'POST',
        credentials: 'include',
      });
      navigate('/partners/login');
    } catch (err) {
      console.error('Ошибка выхода:', err);
    }
  };

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGamePartner || !profile) {
      setError('Выберите игру и проверьте баланс');
      return;
    }
    
    const requiredKeys = selectedGamePartner.price * createForm.keyCount;
    const availableKeys = Number(profile.balance || 0);
    
    if (availableKeys < requiredKeys) {
      setError(`Недостаточно баланса. Доступно: ${availableKeys}, требуется: ${requiredKeys}`);
      return;
    }
    
    try {
      const response = await fetch('/api/partner/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...createForm,
          keyCount: createForm.keyCount,  // Убедимся, что keyCount передаётся
          gameTitle: selectedGamePartner.title  // Используем title из selected
        }),
      });

      if (response.ok) {
        setCreateForm({ gameTitle: '', notion: '', keyCount: 1, urlButton: '' });
        setSelectedGamePartner(null);
        loadData(); // Refetch для обновления баланса и списка
        setError('');
      } else {
        const data = await response.json();
        setError(data.message || 'Ошибка создания игры');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Можно добавить уведомление об успешном копировании
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language);
  };

  const calculateEndDate = (startDate: string, days: number) => {
    const start = new Date(startDate);
    const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
    return end.toLocaleDateString(i18n.language);
  };

  const getGameUrl = (key: string) => {
    return `${window.location.origin}/login?key=${key}`;
  };

  const getPlayUrl = (buildId: string) => {
    return `${window.location.origin}/game?buildId=${buildId}`;
  };

  const handleGameClick = (buildId: string) => {
    window.open(getPlayUrl(buildId), '_blank');
  };

  const handleEditorClick = (key: string) => {
    window.open(getGameUrl(key), '_blank');
  };

  const calculateMonthlyStats = (games: PartnerGame[]) => {
    const now = new Date();
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    
    return games.filter(game => {
      const gameDate = new Date(game.createdAt);
      return gameDate >= monthAgo;
    }).length;
  };

  const handleExportExcel = () => {
    const exportData = games.map(game => ({
      [t('partner.xlsx.columns.gameName')]: game.name || game.gameName,
      [t('partner.xlsx.columns.email')]: game.email || '-',
      [t('partner.xlsx.columns.notion')]: game.notion || '-',
      [t('partner.xlsx.columns.createdAt')]: formatDate(game.createdAt),
      [t('partner.xlsx.columns.key')]: game.key,
      [t('partner.xlsx.columns.runCount')]: game.runCount,
      [t('partner.xlsx.columns.activateDate')]: game.activateDate ? formatDate(game.activateDate) : '-',
      [t('partner.xlsx.columns.editUntil')]: (game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.editDayCount) : '-',
      [t('partner.xlsx.columns.disableDate')]: (game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.gameDayCount) : '-',
      [t('partner.xlsx.columns.status')]: game.isActive ? t('partner.status.active') : t('partner.status.inactive'),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('partner.xlsx.sheetName'));
    XLSX.writeFile(wb, `${t('partner.xlsx.fileName')}.xlsx`);
  };

  // Фильтрация игр по периоду
  const filteredGames = games.filter(game => {
    if (!dateFrom && !dateTo) return true;
    const created = new Date(game.createdAt);
    let from = dateFrom ? new Date(dateFrom) : null;
    let to = dateTo ? new Date(dateTo) : null;
    if (from && created < from) return false;
    if (to) {
      // включительно до конца дня
      to.setHours(23,59,59,999);
      if (created > to) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="partner-dashboard">
        <div className="partner-dashboard__loading">{t('partner.loading')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="partner-dashboard">
        <div className="partner-dashboard__error">{t('partner.profileLoadError')}</div>
      </div>
    );
  }

  return (
    <div className="partner-dashboard">
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: { xs: 2, lg: 0 }, padding: { xs: 1, lg: 2 } }} className="partner-dashboard__header">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'center', lg: 'flex-start' }, gap: 1, order: { xs: 1, lg: 0 } }} className="partner-dashboard__header-left">
          <div className="partner-dashboard__header-title" style={{ fontSize: '1.1rem' }}>{t('partner.headerTitle')}</div>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, justifyContent: 'center' }} className="partner-dashboard__stats-row">
            <div className="partner-dashboard__stat-block-inline">
              <span className="partner-dashboard__stat-label-inline" style={{ fontSize: '0.6rem' }}>{t('partner.createdThisMonth')}</span>
              <span className="partner-dashboard__stat-value-inline" style={{ fontSize: '0.9rem' }}>{monthlyStats}</span>
            </div>
            <div className="partner-dashboard__stat-block-inline">
              <span className="partner-dashboard__stat-label-inline" style={{ fontSize: '0.6rem' }}>{t('partner.balance')}</span>
              <span className="partner-dashboard__stat-value-inline" style={{ fontSize: '0.9rem' }}>{profile.balance}</span>
            </div>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', lg: 'flex-end' }, flexWrap: 'wrap', order: { xs: -1, lg: 0 } }} className="partner-dashboard__header-user">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              style={{ backgroundColor: 'white', color: 'black', width: '50px' }}
            >
              <option value="en-EN">EN</option>
              <option value="ru-RU">RU</option>
              <option value="es-ES">ES</option>
              <option value="de-DE">DE</option>
              <option value="fr-FR">FR</option>
            </select>
            <button onClick={handleLogout} className="partner-dashboard__logout" style={{ fontSize: '0.7rem', padding: '2px 4px' }}>{t('global.LOGOUT')}</button>
          </Box>
          <span style={{ fontSize: '0.6rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>{profile.email}</span>
        </Box>
      </Box>

      <div className="partner-dashboard__content">
        {/* Форма создания игры */}
        <div className="partner-dashboard__create-section">
          <div className="partner-dashboard__section-title">{t('partner.createSectionTitle')}</div>
          <form onSubmit={handleCreateGame} className="partner-dashboard__create-form">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'flex-start' } }} className="partner-dashboard__form-row">
              <div className="partner-dashboard__field partner-dashboard__field--game-select" style={{ flex: '2 1 200px' }}>
                <label>{t('partner.selectGameLabel')}</label>
                <Autocomplete
                  options={availableGames}
                  getOptionLabel={(option) => option.name || option.title}
                  value={availableGames.find(game => game.title === createForm.gameTitle) || null}
                  onChange={(event, newValue) => {
                    setCreateForm({ ...createForm, gameTitle: newValue?.title || '' });
                    setSelectedGamePartner(newValue || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('partner.selectGameLabel')}
                      size="small"
                      required
                      sx={{ width: '100%' }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                  sx={{ width: '100%' }}
                />
              </div>
              
              <div className="partner-dashboard__field" style={{ flex: '1 1 150px' }}>
                <label>{t('partner.notionOptionalLabel')}</label>
                <input
                  type="text"
                  value={createForm.notion}
                  onChange={(e) => setCreateForm({ ...createForm, notion: e.target.value })}
                  placeholder={t('partner.notionPlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="partner-dashboard__field" style={{ flex: '1 1 150px' }}>
                <label>{t('partner.urlButtonOptionalLabel')}</label>
                <input
                  type="url"
                  value={createForm.urlButton}
                  onChange={(e) => setCreateForm({ ...createForm, urlButton: e.target.value })}
                  placeholder={t('partner.urlButtonPlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="partner-dashboard__field" style={{ flex: '1 1 100px' }}>
                <label>{t('partner.keyCountLabel')}</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={createForm.keyCount}
                  onChange={(e) => setCreateForm({ ...createForm, keyCount: parseInt(e.target.value) || 1 })}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="partner-dashboard__field" style={{ flex: '0 0 auto' }}>
                <button type="submit" className="partner-dashboard__create-btn">
                  {t('partner.createKey')}
                </button>
              </div>
            </Box>
          </form>

          {selectedGamePartner && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, justifyContent: 'center', width: '100%', maxWidth: '400px', margin: '0 auto', paddingTop:'10px' }}>
              {selectedGamePartner.startGameImageExampleUrl && (
                <img
                  src={Array.isArray(selectedGamePartner.startGameImageExampleUrl) ? selectedGamePartner.startGameImageExampleUrl[0] : selectedGamePartner.startGameImageExampleUrl}
                  alt="Start preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreviewPartner(Array.isArray(selectedGamePartner.startGameImageExampleUrl) ? selectedGamePartner.startGameImageExampleUrl[0] : selectedGamePartner.startGameImageExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              {selectedGamePartner.endGameImageExampleUrl && (
                <img
                  src={Array.isArray(selectedGamePartner.endGameImageExampleUrl) ? selectedGamePartner.endGameImageExampleUrl[0] : selectedGamePartner.endGameImageExampleUrl}
                  alt="End preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreviewPartner(Array.isArray(selectedGamePartner.endGameImageExampleUrl) ? selectedGamePartner.endGameImageExampleUrl[0] : selectedGamePartner.endGameImageExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              {selectedGamePartner.gameTileExampleUrl && (
                <img
                  src={Array.isArray(selectedGamePartner.gameTileExampleUrl) ? selectedGamePartner.gameTileExampleUrl[0] : selectedGamePartner.gameTileExampleUrl}
                  alt="Tile preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreviewPartner(Array.isArray(selectedGamePartner.gameTileExampleUrl) ? selectedGamePartner.gameTileExampleUrl[0] : selectedGamePartner.gameTileExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </Box>
          )}

          {selectedPreviewPartner && (
            <Dialog open onClose={() => setSelectedPreviewPartner(null)} maxWidth="md" fullWidth>
              <DialogContent>
                <img src={selectedPreviewPartner} style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} alt="Preview" />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedPreviewPartner(null)}>Закрыть</Button>
              </DialogActions>
            </Dialog>
          )}
        </div>

        {error && (
          <div className="partner-dashboard__error">
            {error}
          </div>
        )}

        <div className="partner-dashboard__games-toolbar">
          <button className="partner-dashboard__export-btn" onClick={handleExportExcel}>
            {t('partner.exportExcel')}
          </button>
        </div>

        <div className="partner-dashboard__games">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, marginBottom: 2 }}>
            <div className="partner-dashboard__section-title">{`${t('partner.keysTitle')} (${filteredGames.length})`}</div>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: '1.15rem' }}>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                style={{ width: 'auto' }}
              />
              <span style={{fontSize: '1.15rem'}}>—</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                style={{ width: 'auto' }}
              />
            </Box>
          </Box>
          
          {filteredGames.length === 0 ? (
            <div className="partner-dashboard__empty">
              {t('partner.noGamesInPeriod')}
            </div>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <div className="partner-dashboard__table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('partner.table.gameName')}</th>
                      <th>{t('partner.table.email')}</th>
                      <th>{t('partner.table.notion')}</th>
                      <th>{t('partner.table.createdAt')}</th>
                      <th>{t('partner.table.key')}</th>
                      <th>{t('partner.table.links')}</th>
                      <th>{t('partner.table.runCount')}</th>
                      <th>{t('partner.table.activateDate')}</th>
                      <th>{t('partner.table.editUntil')}</th>
                      <th>{t('partner.table.disableDate')}</th>
                      <th>{t('partner.table.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGames.map((game) => (
                      <tr key={game._id} className={!game.isActive ? 'partner-dashboard__row--inactive' : ''}>
                        <td>{game.name || game.gameName}</td>
                        <td>{game.email || '-'}</td>
                        <td>{game.notion || '-'}</td>
                        <td>{formatDate(game.createdAt)}</td>
                        <td>
                          <code>{game.key}</code>
                          <button 
                            onClick={() => copyToClipboard(game.key)}
                            className="partner-dashboard__copy-btn"
                          >
                            {t('partner.copy')}
                          </button>
                        </td>
                        <td>
                          <div className="partner-dashboard__links">
                            <div className="partner-dashboard__link-group">
                              <button 
                                onClick={() => handleEditorClick(game.key)}
                                className="partner-dashboard__link-btn"
                              >
                                {t('partner.editor')}
                              </button>
                              <button 
                                onClick={() => copyToClipboard(getGameUrl(game.key))}
                                className="partner-dashboard__copy-icon-btn"
                                title={t('partner.copyEditorLink')}
                              >
                                📋
                              </button>
                            </div>
                            {game.isCreated && game.gameBuild && (
                              <div className="partner-dashboard__link-group">
                                <button 
                                  onClick={() => handleGameClick(game.gameBuildId)}
                                  className="partner-dashboard__link-btn"
                                >
                                  {t('partner.game')}
                                </button>
                                <button 
                                  onClick={() => copyToClipboard(getPlayUrl(game.gameBuildId))}
                                  className="partner-dashboard__copy-icon-btn"
                                  title={t('partner.copyGameLink')}
                                >
                                  📋
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="partner-dashboard__runs">{game.runCount}</td>
                        <td>{game.activateDate ? formatDate(game.activateDate) : '-'}</td>
                        <td>{(game.gameBuild && game.gameBuild.createdAt) ? calculateEndDate(game.gameBuild.createdAt, game.editDayCount) : '-'}</td>
                        <td>{game.activateDate ? formatGameEndDate(new Date(game.activateDate), game.gameDayCount) : '-'}</td>
                        <td>
                          <span className={`partner-dashboard__status partner-dashboard__status--${game.isActive ? 'active' : 'inactive'}`}>
                            {game.isActive ? t('partner.status.active') : t('partner.status.inactive')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Box>
          )}
        </div>
      </div>


    </div>
  );
};

export default Dashboard; 